<template>
  <side-panel
    class="side-panel--cart"
    :class="{'side-panel--cart-mobile': isMobile}"
    :is-visible="isOpen"
    @closeSidePanel="close"
    @click="clearCloseTimer"
    @keyup.tab="clearCloseTimer"
  >
    <div
      class="add-to-cart-panel"
      :class="{
        'add-to-cart-panel--with-total-savings': totalSavings,
        'add-to-cart-panel--not-free-shipping': !isShippingFree,
      }"
    >
      <header
        class="add-to-cart-panel__headline"
        :aria-label="$t('addtocart.head')"
      >
        {{ $t('addtocart.head') }}
      </header>

      <section class="add-to-cart-panel__content">
        <div
          class="add-to-cart-panel__list-container"
          v-if="!isMobile"
          @scroll.once="clearCloseTimer"
        >
          <transition-group
            tag="div"
            name="vue-animation--list"
            class="add-to-cart-panel__list"
            role="list"
            :aria-label="$t('checkout.ordersummary.basket.headline')"
          >
            <order-position
              v-for="item in basketItems"
              :key="item.itemId"
              :item="item"
              :style="{'transition-duration': '.3s'}"
              class="add-to-cart-panel__product"
              :show-reduced="true"
              :is-editable="!item.freeItem"
              disable-qty
              role="listitem"
            />
            <order-position
              v-for="item in freeItems"
              :key="item.itemId"
              :item="item"
              :style="{'transition-duration': '.3s'}"
              class="add-to-cart-panel__product"
              disable-qty
              :is-editable="false"
              role="listitem"
            />
          </transition-group>
        </div>

        <section class="add-to-cart-panel__footer">
          <div
            class="add-to-cart-panel__delivery add-to-cart-panel__cols"
            role="row"
            :aria-label="$t('checkout.basket.deliveryStatementText')"
          >
            <div class="add-to-cart-panel__col">
              {{ $t('checkout.basket.deliveryStatementText') }}
            </div>
            <div
              class="add-to-cart-panel__col"
              :class="{'add-to-cart-panel__col--strong': summary.shipping === freePrice}"
            >
              {{ shippingCostText }}
            </div>
          </div>
          <div
            class="add-to-cart-panel__grand-total add-to-cart-panel__cols"
            role="row"
            :aria-label="$t('checkout.basket.invoiceDetails.totalAmount')"
          >
            <div class="add-to-cart-panel__col">
              {{ $t('checkout.basket.invoiceDetails.totalAmount') }}
              <span class="add-to-cart-panel__tax">
                {{ $t('checkout.basket.invoiceDetails.inclVAT') }}
              </span>
            </div>
            <div class="add-to-cart-panel__col">
              {{ summary.grandTotal }}
            </div>
          </div>
          <div
            v-if="totalSavings"
            class="add-to-cart-panel__total-savings add-to-cart-panel__cols"
            role="row"
            :aria-label="$t('checkout.basket.invoiceDetails.totalSavings')"
          >
            <div class="add-to-cart-panel__col">
              {{ $t('checkout.basket.invoiceDetails.totalSavings') }}
            </div>
            <div class="add-to-cart-panel__col">
              {{ summary.totalSavings }}
            </div>
          </div>
          <div
            v-if="minimumOrderText"
            class="add-to-cart-panel__upsell-promotion add-to-cart-panel__upsell-promotion--minimum-order"
            v-html="minimumOrderText"
            role="alert"
          />
          <div
            v-else-if="notFreeShippingText"
            class="add-to-cart-panel__upsell-promotion add-to-cart-panel__upsell-promotion--not-free-shipping"
            v-html="notFreeShippingText"
            role="alert"
          />
        </section>
        <section class="add-to-cart-panel__buttons">
          <a
            class="add-to-cart-panel__cart-btn | button button--secondary"
            :href="basketUrl"
          >
            {{ $t('addtocart.cartButton') }}
          </a>
          <a
            class="add-to-cart-panel__checkout-btn | button button--primary"
            :href="checkoutUrl"
            @click="clickButton()"
          >
            {{ $t('addtocart.checkoutButton') }}
          </a>
          <div
            class="add-to-cart-panel__close-link | button button--link"
            @click="close()"
            role="button"
          >
            {{ $t('addtocart.closeButton') }}
          </div>
        </section>
      </section>
    </div>
  </side-panel>
</template>

<script setup lang="ts">
  import {onMounted, ref, computed, watch} from 'vue';
  import {useIsMobile} from '../../../bps/utilities/composables/isMobile/isMobile';
  import basketStore from '../../../bps/stores/basket/BasketStore';
  import basketService from '../../../bps/services/basket/BasketService';
  import pigeon from '../../../bps/utilities/js/pigeon/pigeon';
  import jsGlobalConfig from '../../../bps/globals/jsGlobalConfig';
  import type BasketItem from '../../../bps/types/BasketItem';
  import SidePanel from '../../../bps/components/sidePanel/SidePanel.vue';
  import OrderPosition from '../../../bps/components/orderPosition/orderPosition.vue';
  import tracking from '@/bps/utilities/js/tracking/Tracking';
  import diContainer from '@/bps/utilities/js/diContainer/diContainer';
  import {t} from '@/bps/utilities/js/translations/translations';
  const priceHelper = diContainer.get('priceHelper');

  const props = withDefaults(defineProps<{
    secondsToShow?: number,
    showTotalSavings?: boolean
  }>(), {
    secondsToShow: 5,
    showTotalSavings: false
  });

  onMounted(() => {
    pigeon.subscribe('productaddedToCart:updateCounter', () => {
      open(true);
    });
  });

  const {isMobile} = useIsMobile('tablet');

  const freePrice = '0';
  let timeoutId = null;
  const basketUrl: string = jsGlobalConfig().getBasketUrl();
  const checkoutUrl: string = jsGlobalConfig().getCheckoutUrl();

  const isOpen = ref(false);
  const isLoading = ref(false);
  let tmpBasketItems = ref<BasketItem[]|null>(null);

  const basketItems = computed(() => {
    return tmpBasketItems.value || [...basketStore.items].reverse();
  });

  const freeItems = computed(() => {
    return basketStore.freeItems;
  });

  const summary = computed(() => {
    return basketStore.summary;
  });

  const totalSavings = computed(() => {
    return summary.value.totalSavings && props.showTotalSavings;
  });

  const isShippingFree = computed(() => {
    return priceHelper.isZero(summary.value.shipping);
  });

  const shippingCostText = computed(() => {
    return summary.value.shipping === freePrice ? t('checkout.basket.layer.freeShipping') : summary.value.shipping;
  });

  const notFreeShippingText = computed(() => {
    return basketStore.freeShippingFulFilled ? null : t('checkout.basket.deliveryCostsPromotion.upselling', [summary.value.leftUntilFreeShippingLimit]);
  })

  const minimumOrderText = computed(() => {
    // if already fulfilled or marketplace is active
    if (basketStore.minimumOrderValueFulFilled || jsGlobalConfig().getPartnerUrl()) {
      return null;
    }
    const minOrderLimit = summary.value.leftUntilMinOrderLimit;
    const noBottlesLimit = summary.value.leftUntilNoBottlesLimit;
    if ((minOrderLimit && minOrderLimit !== freePrice) && (noBottlesLimit && noBottlesLimit !== freePrice)) {
      if (summary.value.leftUntilNoBottlesLimit === '1') {
        return t('checkout.basket.minOrderLimit.one', [summary.value.leftUntilNoBottlesLimit, summary.value.leftUntilMinOrderLimit]);
      } else {
        return t('checkout.basket.minOrderLimit.other', [summary.value.leftUntilNoBottlesLimit, summary.value.leftUntilMinOrderLimit]);
      }
    }
    if (summary.value.leftUntilNoBottlesLimit === '1') {
      return t('checkout.basket.minOrderLimit.one', [summary.value.leftUntilNoBottlesLimit]);
    } else {
      const left = summary.value.leftUntilMinOrderLimit != null ? summary.value.leftUntilMinOrderLimit : summary.value.leftUntilNoBottlesLimit;
      return t('checkout.basket.minOrderLimit.other', [left]);
    }
  });

  // watch basketItems is length <= 0 then close the panel
  watch(basketItems, (newVal) => {
    if (newVal.length <= 0) {
      close();
    }
  });

  function open(withAddAnimation = false) {
    if (mayOpen() === false) {
      return;
    }

    clearCloseTimer();

    if (basketItems.value.length > 0) {
      setCloseTimer(props.secondsToShow * 1000);
      isOpen.value = true;
    }

    if (withAddAnimation) {
      tmpBasketItems.value = [...basketStore.items].reverse();
    }

    updateData().then(() => {
      if (basketStore.items.length === 0) {
        close();
        return;
      }
      else if (basketStore.items.length >= 2 && withAddAnimation) {
        tmpBasketItems.value = [...basketStore.items].reverse().slice(1);
      }

      if (location.href.indexOf(basketUrl) < 0) {
        setCloseTimer(props.secondsToShow * 1000);
        isOpen.value = true;
      }

      setTimeout(() => {
        tmpBasketItems.value = null;
      }, 300);

      // Make sure that the layer is open
      pigeon.publish('addToCartLayer:open');
    });

    pigeon.publish('addToCartLayer:open');
  }

  function close() {
    clearCloseTimer();
    isOpen.value = false;
    pigeon.publish('addToCartLayer:close');
  }

  function clearCloseTimer() {
    window.clearTimeout(timeoutId);
    timeoutId = null;
  }

  function setCloseTimer(time: number) {
    clearCloseTimer();
    timeoutId = setTimeout(close, time);
  }

  async function updateData(): Promise<any> {
    if (isLoading.value) {
      return new Promise(() => {
      });
    }

    isLoading.value = true;
    return basketService.loadMiniBasketData().then(() => {
      isLoading.value = false;
    })
  }

  function mayOpen(): boolean {
    const url = location.href;
    return url.indexOf(basketUrl) < 0 && url.indexOf(checkoutUrl) < 0 && url.indexOf('env=test') < 0;
  }

  function clickButton() {
    tracking.fireCheckoutStep('checkout_step_1', 'mini-cart', basketStore.summary.grandTotalValue);
  }

</script>
